import { UseMutationOptions } from 'react-query';
import appConfig from '@frontend/env';
import { http } from '@frontend/fetch';
import { useMutation } from '@frontend/react-query-helpers';
import { SchemaPaymentsCommandsService } from '@frontend/schema';
import {
  PaymentClientSecret,
  CreatePaymentIntentBody,
  CreatePaymentIntentResponse,
  PaymentRequest,
  LocationPortalResponse,
  ValidateACHTransactionBalanceResponse,
  ValidateACHTransactionBalanceRequest,
  CreatePaymentIntentSyncResponse,
  LocationBranding,
} from './types';

const { PAY_BACKEND_API } = appConfig;

export const getPaymentRequest = (requestId: string) =>
  http.getData<PaymentRequest>(`${PAY_BACKEND_API}/payments/requests/${requestId}`, {
    skipValidation: true,
    headers: { accept: 'application/json' },
  });

export const getLocationLogo = (locationId: string) =>
  http.get<LocationBranding>(`${PAY_BACKEND_API}/payments/locations/${locationId}/brand-details`, {
    skipValidation: true,
    headers: { accept: 'application/json' },
  });

type CreateOBPPaymentIntentProps = Omit<CreatePaymentIntentProps, 'url'> & {
  id: string;
  captchaToken: string;
};
export const createOBPPaymentIntent = ({ id, requestBody, captchaToken }: CreateOBPPaymentIntentProps) =>
  createPaymentIntent({
    url: `${PAY_BACKEND_API}/payments/v2/portal/${id}/payments`,
    requestBody,
    captchaToken,
  });

type CreatePaymentIntentProps = {
  url: string;
  requestBody: CreatePaymentIntentBody;
  locationId?: string;
  captchaToken?: string;
};

export const createPaymentIntent = ({ url, requestBody, locationId, captchaToken }: CreatePaymentIntentProps) =>
  http.post<CreatePaymentIntentResponse, CreatePaymentIntentBody>(url, requestBody, {
    skipValidation: true,
    headers: {
      'Content-Type': 'application/json',
      ...(locationId && { [LOCATION_ID_HEADER]: locationId }),
      ...(!!captchaToken && { 'X-Weave-Recaptcha-Response-Token': captchaToken }),
    },
  });

export const createPaymentIntentSync = ({ url, requestBody, captchaToken }: CreatePaymentIntentProps) =>
  http.post<CreatePaymentIntentSyncResponse, CreatePaymentIntentBody>(url, requestBody, {
    skipValidation: true,
    headers: {
      'Content-Type': 'application/json',
      ...(!!captchaToken && { 'X-Weave-Recaptcha-Response-Token': captchaToken }),
    },
  });

type AuthedClientSecret = {
  url: string;
  locationId?: string;
  authToken: string;
};

export const getPaymentClientSecretWithAuthToken = ({ url, locationId, authToken }: AuthedClientSecret) =>
  http.getData<PaymentClientSecret>(url, {
    skipValidation: true,
    headers: {
      accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
      ...(locationId && { [LOCATION_ID_HEADER]: locationId }),
    },
  });

export const getPaymentClientSecret = (location: string, url: string) =>
  http.getData<PaymentClientSecret>(
    url,
    addLocationHeader(location, {
      accept: 'application/json',
      'Content-Type': 'application/json',
    })
  );

export const validateBirthday = (url: string, birthday: string) =>
  http.post<unknown>(
    url,
    { data: { birthday } },
    { skipValidation: true, headers: { 'Content-Type': 'application/json' } }
  );

const LOCATION_ID_HEADER = 'Location-Id';
const addLocationHeader = (locationId: string, headers: Record<string, string> = {}) => ({
  headers: { [LOCATION_ID_HEADER]: locationId, ...headers },
});

// Get location details for Location Portal
export const getLocationPortalDetails = (locationSlug: string) =>
  http.getData<LocationPortalResponse>(`${PAY_BACKEND_API}/payments/portal/${locationSlug}`, {
    skipValidation: true,
  });

export const makePayment = (planDetails: CreatePaymentIntentBody, locationId: string, invoiceId?: string) => {
  const makePaymentUrl = invoiceId
    ? `payments/v2/portal/requests/${invoiceId}/payments`
    : `payments/v2/locations/${locationId}/payments`;
  return http.post<CreatePaymentIntentResponse>(makePaymentUrl, { ...planDetails }, addLocationHeader(locationId));
};

export const makePaymentSync = (planDetails: CreatePaymentIntentBody, locationId: string, invoiceId?: string) => {
  const makePaymentUrl = invoiceId
    ? `payments/v2/portal/requests/${invoiceId}/payments:sync`
    : `payments/v2/locations/${locationId}/payments:sync`;
  return http.post<CreatePaymentIntentSyncResponse>(makePaymentUrl, { ...planDetails }, addLocationHeader(locationId));
};

export const useValidateACHTransactionBalance = (
  options?: UseMutationOptions<ValidateACHTransactionBalanceResponse, unknown, ValidateACHTransactionBalanceRequest>
) => {
  return useMutation({
    mutationFn: (request: ValidateACHTransactionBalanceRequest) =>
      SchemaPaymentsCommandsService.ValidateACHTransactionBalance(request),
    ...options,
  });
};
